/**
 * PortalPage - Theme Selector Landing Page
 *
 * Dark, immersive entry point where visitors choose their viewing experience.
 * Features persona-adaptive copy based on referrer detection.
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ShaderBackground } from '@/components/portal/ShaderBackground'
import { MagneticText } from '@/components/portal/MagneticText'
import { ThemeCard } from '@/components/portal/ThemeCard'
import { useReferrerPersona } from '@/hooks/useReferrerPersona'
import { LAYOUT_THEME_CONFIGS, type LayoutTheme } from '@/types/layout-theme'
import '@/styles/portal.css'

// Theme display order and descriptors
const THEME_DISPLAY: Array<{
  theme: LayoutTheme
  descriptor: string
}> = [
  { theme: 'mac-studio', descriptor: 'Desktop OS' },
  { theme: 'modern-cards', descriptor: 'Clean & Light' },
  { theme: 'matrix-terminal', descriptor: 'Hacker Aesthetic' },
  { theme: 'mac-os-classic', descriptor: 'Retro Nostalgia' },
  { theme: 'executive-dashboard', descriptor: 'Data Dense' },
]

export default function PortalPage() {
  const { content } = useReferrerPersona()
  const cursorRef = useRef<HTMLDivElement>(null)
  const [scrollProgress, setScrollProgress] = useState(0)

  // Custom cursor logic
  const handleMouseMove = useCallback((e: MouseEvent) => {
    const cursor = cursorRef.current
    if (!cursor) return

    cursor.style.left = `${e.clientX}px`
    cursor.style.top = `${e.clientY}px`
    cursor.classList.add('visible')
  }, [])

  const handleMouseEnterInteractive = useCallback(() => {
    cursorRef.current?.classList.add('active')
  }, [])

  const handleMouseLeaveInteractive = useCallback(() => {
    cursorRef.current?.classList.remove('active')
  }, [])

  // Scroll progress for light bar
  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY
    const docHeight = document.body.scrollHeight - window.innerHeight
    const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0
    setScrollProgress(progress)
  }, [])

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('scroll', handleScroll)

    // Add hover listeners to interactive elements
    const interactables = document.querySelectorAll(
      '.theme-card, .portal-footer__contact, .portal-footer__social-link, .magnetic-wrap'
    )
    interactables.forEach(el => {
      el.addEventListener('mouseenter', handleMouseEnterInteractive)
      el.addEventListener('mouseleave', handleMouseLeaveInteractive)
    })

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('scroll', handleScroll)
      interactables.forEach(el => {
        el.removeEventListener('mouseenter', handleMouseEnterInteractive)
        el.removeEventListener('mouseleave', handleMouseLeaveInteractive)
      })
    }
  }, [handleMouseMove, handleScroll, handleMouseEnterInteractive, handleMouseLeaveInteractive])

  return (
    <>
      <Helmet>
        <title>Agustin Musi | CTO & Platform Architect | Zurich</title>
        <meta
          name="description"
          content="CTO and platform architect based in Zurich. 30 years building systems where small teams achieve enterprise scale. Explore my work in your preferred visual style."
        />
        <meta property="og:title" content="Agustin Musi - Choose Your Experience" />
        <meta
          property="og:description"
          content="Platform engineering, AI-native architectures, and 10x cost optimization. Select how you'd like to explore."
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Agustin Musi | CTO" />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Person',
            name: 'Agustin Musi',
            jobTitle: 'CTO & Platform Architect',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Zurich',
              addressCountry: 'Switzerland',
            },
            url: 'https://agustinmusi.com',
            sameAs: [
              'https://github.com/auge2u',
              'https://github.com/a2m2c',
              'https://linkedin.com/in/agustinmusi',
            ],
            knowsAbout: [
              'Platform Engineering',
              'Cloud Architecture',
              'AI-Native Systems',
              'Cost Optimization',
              'Edge Computing',
            ],
            hasOccupation: {
              '@type': 'Occupation',
              name: 'Chief Technology Officer',
              occupationalCategory: '15-1211.00',
            },
          })}
        </script>
      </Helmet>

      <div className="portal-page">
        {/* Three.js Background */}
        <ShaderBackground opacity={0.6} />

        {/* Custom Cursor */}
        <div ref={cursorRef} className="portal-cursor" />

        {/* Scroll Progress Light */}
        <div className="portal-scroll-light" style={{ width: `${scrollProgress}%` }} />

        {/* Header */}
        <header className="portal-header">
          <MagneticText as="span" className="portal-brand">
            AM.
          </MagneticText>
          <div className="portal-status">
            <span className="portal-status__dot" />
            <span>Zurich &bull; Online</span>
          </div>
        </header>

        {/* Main Content */}
        <main className="portal-main">
          {/* Hero Section */}
          <section className="portal-hero">
            <h1 className="portal-hero__name portal-animate-in">
              <span className="portal-hero__name-line portal-hero__name-line--outlined">
                Agustin
              </span>
              <span className="portal-hero__name-line portal-hero__name-line--filled">
                Musi
              </span>
            </h1>

            <span className="portal-hero__label portal-animate-in portal-animate-in--delay-2">
              {content.label}
            </span>

            <p className="portal-hero__headline portal-animate-in portal-animate-in--delay-3">
              {content.headline}
            </p>
          </section>

          {/* Theme Selector */}
          <section className="portal-selector">
            <span className="portal-selector__label portal-animate-in portal-animate-in--delay-4">
              // CHOOSE YOUR EXPERIENCE
            </span>

            <div className="portal-selector__grid">
              {THEME_DISPLAY.map(({ theme, descriptor }, index) => (
                <div
                  key={theme}
                  className={`portal-animate-in portal-animate-in--delay-${Math.min(index + 5, 5)}`}
                >
                  <ThemeCard
                    theme={theme}
                    name={LAYOUT_THEME_CONFIGS[theme].name}
                    descriptor={descriptor}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* Footer */}
          <footer className="portal-footer">
            <span className="portal-footer__label">// INITIATE CONTACT</span>

            <MagneticText
              as="a"
              href="mailto:contact@agustinmusi.com"
              className="portal-footer__contact"
            >
              contact@agustinmusi.com
            </MagneticText>

            <div className="portal-footer__social">
              <MagneticText
                as="a"
                href="https://github.com/auge2u"
                className="portal-footer__social-link"
              >
                @auge2u
              </MagneticText>
              <MagneticText
                as="a"
                href="https://github.com/a2m2c"
                className="portal-footer__social-link"
              >
                @a2m2c
              </MagneticText>
              <MagneticText
                as="a"
                href="https://linkedin.com/in/agustinmusi"
                className="portal-footer__social-link"
              >
                LinkedIn
              </MagneticText>
            </div>

            <div className="portal-footer__bottom">
              <span>&copy; {new Date().getFullYear()} Agustin Musi</span>
              <div className="portal-footer__legal">
                <Link to="/privacy">Privacy</Link>
                <Link to="/terms">Terms</Link>
              </div>
            </div>
          </footer>
        </main>

        {/* Hidden SEO/LLM Content */}
        <div className="portal-sr-only" aria-hidden="false">
          <h1>Agustin Musi - CTO and Platform Architect</h1>
          <p>
            Based in Zurich, Switzerland. 30 years experience building platforms where 5-person
            teams achieve 50-person capabilities.
          </p>
          <h2>Expertise</h2>
          <ul>
            <li>Platform Engineering & DevOps</li>
            <li>AI-Native Architectures</li>
            <li>Edge Computing (Cloudflare Workers)</li>
            <li>Cost Optimization (10x reductions)</li>
            <li>MCP Tool Orchestration</li>
          </ul>
          <h2>Available Views</h2>
          <ul>
            <li>Mac Studio - macOS-inspired desktop interface with draggable windows</li>
            <li>Modern Cards - Clean, contemporary card-based layout</li>
            <li>Matrix Terminal - Retro hacker aesthetic with CLI-inspired design</li>
            <li>Classic Mac OS - Nostalgic System 7 style interface</li>
            <li>Executive Dashboard - Data-dense metrics and charts view</li>
          </ul>
        </div>
      </div>
    </>
  )
}
